import { isOfType } from '../../../../ts-utils';
import { ensureMemberHasRightTo } from '../../members/members-actions';
import { beforeCrudErrorToMessage, getMessageFromError } from '../comments-errors';
import { extractMentions } from '../../../utils/extract-mentions';
import { isReplyStateIN } from './reply-types';
import { startRequestTimer } from '../../../utils/request-timer';
import { addNewHistoryItem } from '../history/history-actions';
export const INIT_REPLY = 'INIT_REPLY';
export const EDIT_REPLY = 'EDIT_REPLY';
export const CANCEL_CREATING_REPLY = 'CANCEL_CREATING_REPLY';
export const CANCEL_EDITING_REPLY = 'CANCEL_EDITING_REPLY';
export const EDIT_REPLY_CREATE_ERROR = 'EDIT_REPLY_CREATE_ERROR';
export const CREATE_REPLY_REQUEST = 'CREATE_REPLY_REQUEST';
export const CREATE_REPLY_FAILURE = 'CREATE_REPLY_FAILURE';
export const CREATE_REPLY_SUCCESS = 'CREATE_REPLY_SUCCESS';
export const UPDATE_REPLY_REQUEST = 'UPDATE_REPLY_REQUEST';
export const UPDATE_REPLY_FAILURE = 'UPDATE_REPLY_FAILURE';
export const UPDATE_REPLY_SUCCESS = 'UPDATE_REPLY_SUCCESS';
export const DELETE_REPLY_REQUEST = 'DELETE_REPLY_REQUEST';
export const DELETE_REPLY_FAILURE = 'DELETE_REPLY_FAILURE';
export const DELETE_REPLY_SUCCESS = 'DELETE_REPLY_SUCCESS';
export const createReplyRequest = (payload) => ({
    type: CREATE_REPLY_REQUEST,
    payload,
});
export const createReplySuccess = (payload) => ({
    type: CREATE_REPLY_SUCCESS,
    payload,
});
export const createReplyFailure = (payload) => ({
    type: CREATE_REPLY_FAILURE,
    payload,
});
export function createReply({ content, resourceId, targetId, ctxFields, localId, asGuest, authorName, email, }) {
    return async (dispatch, _getState, { commentsService, appDefinitionId, executeBeforeCrud }) => {
        dispatch(createReplyRequest({ resourceId, content, localId, targetId, asGuest, authorName }));
        const permissionResult = await dispatch(ensureMemberHasRightTo({
            userAction: 'CREATE_REPLY',
            resourceId,
            commentId: targetId,
            isGuestFlowChosen: asGuest,
        }));
        if (permissionResult !== 'OK') {
            return dispatch(createReplyFailure({
                resourceId,
                localId,
                message: getMessageFromError(permissionResult === 'ACTION_CANCELLED' ? { type: 'SILENT' } : { type: 'PERMISSION' }, { isCreateCommentAction: true }),
                authorName,
                email,
            }));
        }
        const interceptorResult = await executeBeforeCrud({
            type: 'REPLY',
            operation: 'CREATE',
            content,
        });
        if (interceptorResult.type === 'ERROR') {
            dispatch(createReplyFailure({
                resourceId,
                localId,
                message: beforeCrudErrorToMessage(interceptorResult),
                authorName,
                email,
            }));
            return;
        }
        const requestTimer = startRequestTimer();
        const result = await commentsService.createComment({
            comment: {
                appId: appDefinitionId,
                contextId: ctxFields.contextId,
                contextType: ctxFields.contextType,
                resourceId,
                content: {
                    richContent: interceptorResult.content,
                    mentions: extractMentions(interceptorResult.content),
                },
                parentCommentId: targetId,
                authorName,
            },
            email,
        });
        result.type === 'SUCCESS'
            ? dispatch(createReplySuccess({
                resourceId,
                comment: result.response.comment,
                localId,
                targetId,
                requestDuration: requestTimer.stop(),
                authorName,
            }))
            : dispatch(createReplyFailure({
                resourceId,
                localId,
                message: getMessageFromError(result.error, { isCreateCommentAction: true }),
                targetId,
                authorName,
                email,
            }));
    };
}
export const initReply = (payload) => ({
    type: INIT_REPLY,
    payload,
});
export const cancelCreatingReply = (payload) => ({
    type: CANCEL_CREATING_REPLY,
    payload,
});
export const editReplyCreateError = (payload) => ({
    type: EDIT_REPLY_CREATE_ERROR,
    payload,
});
export const cancelEditingReply = (payload) => ({
    type: CANCEL_EDITING_REPLY,
    payload,
});
export const editReply = (payload) => ({
    type: EDIT_REPLY,
    payload,
});
export const deleteReplyRequest = (payload) => ({
    type: DELETE_REPLY_REQUEST,
    payload,
});
export const deleteReplySuccess = (payload, meta) => ({
    type: DELETE_REPLY_SUCCESS,
    payload,
    meta,
});
export const deleteReplyFailure = (payload) => ({
    type: DELETE_REPLY_FAILURE,
    payload,
});
export function deleteReply({ resourceId, commentId, }) {
    return async (dispatch, getState, { commentsService }) => {
        const resourceState = getState().comments[resourceId];
        if (resourceState?.type !== 'READY') {
            return;
        }
        const replyState = resourceState.commentsById[commentId];
        if (!isReplyStateIN(replyState)) {
            return;
        }
        dispatch(deleteReplyRequest({ resourceId, commentId }));
        const permissionResult = await dispatch(ensureMemberHasRightTo({
            userAction: 'DELETE',
            resourceId,
            commentId,
        }));
        if (permissionResult !== 'OK') {
            return dispatch(deleteReplyFailure({
                resourceId,
                commentId,
                message: getMessageFromError(permissionResult === 'ACTION_CANCELLED' ? { type: 'SILENT' } : { type: 'PERMISSION' }),
            }));
        }
        const result = await commentsService.deleteComment({
            commentId,
            cascade: false,
        });
        result.type === 'SUCCESS'
            ? dispatch(deleteReplySuccess({ resourceId, commentId }, { parentId: replyState.parentId }))
            : dispatch(deleteReplyFailure({ resourceId, commentId, message: getMessageFromError(result.error) }));
    };
}
export const updateReplyRequest = (payload) => ({
    type: UPDATE_REPLY_REQUEST,
    payload,
});
export const updateReplySuccess = (payload) => ({
    type: UPDATE_REPLY_SUCCESS,
    payload,
});
export const updateReplyFailure = (payload) => ({
    type: UPDATE_REPLY_FAILURE,
    payload,
});
export function updateReply({ resourceId, commentId, revisionId, ctxFields, content, currentComment, }) {
    return async (dispatch, getState, { commentsService, appDefinitionId, executeBeforeCrud }) => {
        dispatch(updateReplyRequest({ resourceId, commentId, content }));
        const permissionResult = await dispatch(ensureMemberHasRightTo({
            userAction: 'UPDATE',
            resourceId,
            commentId,
        }));
        if (permissionResult !== 'OK') {
            return dispatch(updateReplyFailure({
                resourceId,
                commentId,
                message: getMessageFromError(permissionResult === 'ACTION_CANCELLED' ? { type: 'SILENT' } : { type: 'PERMISSION' }),
            }));
        }
        const interceptorResult = await executeBeforeCrud({
            type: 'REPLY',
            operation: 'UPDATE',
            content,
        });
        if (interceptorResult.type === 'ERROR') {
            dispatch(updateReplyFailure({
                resourceId,
                commentId,
                message: beforeCrudErrorToMessage(interceptorResult),
            }));
            return;
        }
        const requestTimer = startRequestTimer();
        const result = await commentsService.updateComment({
            comment: {
                appId: appDefinitionId,
                contextId: ctxFields.contextId,
                contextType: ctxFields.contextType,
                commentId,
                resourceId,
                revisionId,
                content: {
                    richContent: interceptorResult.content,
                    mentions: extractMentions(interceptorResult.content),
                },
            },
        });
        if (result.type === 'SUCCESS') {
            const { comment } = result.response;
            dispatch(updateReplySuccess({
                resourceId,
                commentId,
                comment,
                requestDuration: requestTimer.stop(),
            }));
            // revision are valid only for published comments
            if (!comment.draftContent && comment.status === 'PUBLISHED') {
                dispatch(addNewHistoryItem({
                    resourceId,
                    commentId,
                    comment: currentComment,
                    owner: getState().members.currentOwner,
                }));
            }
        }
        else {
            dispatch(updateReplyFailure({ resourceId, commentId, message: getMessageFromError(result.error) }));
        }
    };
}
export const isRepliesCrudAction = isOfType([
    INIT_REPLY,
    CANCEL_CREATING_REPLY,
    EDIT_REPLY_CREATE_ERROR,
    CANCEL_EDITING_REPLY,
    EDIT_REPLY,
    CREATE_REPLY_REQUEST,
    CREATE_REPLY_FAILURE,
    CREATE_REPLY_SUCCESS,
    DELETE_REPLY_REQUEST,
    DELETE_REPLY_FAILURE,
    DELETE_REPLY_SUCCESS,
    UPDATE_REPLY_REQUEST,
    UPDATE_REPLY_FAILURE,
    UPDATE_REPLY_SUCCESS,
]);
//# sourceMappingURL=replies-crud-actions.js.map